import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import {
  getCurrentUser,
  getAuthToken,
  getGymData,
  setGymData,
  clearAuth,
  setAuthToken,
  API,
} from '../lib/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Currency symbol map
const CURRENCY_SYMBOLS = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  INR: '₹',
  AED: 'د.إ',
  SAR: '﷼',
  CAD: 'CA$',
  AUD: 'A$',
  SGD: 'S$',
  JPY: '¥',
};

export const getCurrencySymbol = code => CURRENCY_SYMBOLS[code] || code || '$';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [gym, setGym] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = getAuthToken();
    const currentUser = getCurrentUser();
    const gymData = getGymData();
    if (token && currentUser) {
      setAuthToken(token);
      setUser(currentUser);
      if (gymData) {
        setGym(gymData.profile || gymData);
        setSettings(gymData.settings || null);
      }
      // Refresh from server in background
      axios
        .get(`${API}/profile`)
        .then(({ data }) => {
          const profile = data.profile || {};
          const sett = data.settings || {};
          setGym(profile);
          setSettings(sett);
          setGymData({ profile, settings: sett });
        })
        .catch(() => {
          /* silently keep localStorage data */
        });
    }
    setLoading(false);
  }, []);

  const logout = () => {
    clearAuth();
    setUser(null);
    setGym(null);
    setSettings(null);
    // React Router will handle redirect to /login via PrivateRoute
  };

  const refreshGym = async () => {
    try {
      const { data } = await axios.get(`${API}/profile`);
      const profile = data.profile || {};
      const sett = data.settings || {};
      setGym(profile);
      setSettings(sett);
      setGymData({ profile, settings: sett });
    } catch {
      // silently ignore
    }
  };

  const currencySymbol = getCurrencySymbol(settings?.currency);
  const currencyCode = settings?.currency || 'USD';

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        gym,
        setGym,
        settings,
        setSettings,
        logout,
        loading,
        refreshGym,
        currencySymbol,
        currencyCode,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
